
import { proxy, arrayProxy, fnProxy, fnArrayProxy, t } from "snek-query";

enum UserGender {
    W = "W",
    M = "M",
    D = "D"
}
enum UserVatVerified {
    UNSURE = "UNSURE",
    VERIFIED = "VERIFIED",
    INVALID = "INVALID"
}
enum CustomerSocialImage {
    FACEBOOK = "FACEBOOK",
    INSTAGRAM = "INSTAGRAM",
    TWITTER = "TWITTER",
    LINKEDIN = "LINKEDIN",
    PINTEREST = "PINTEREST"
}
enum CharmDocumentDocumentType {
    BEAUTYREPORT = "BEAUTYREPORT",
    ANSCHREIBEN = "ANSCHREIBEN"
}
enum BaseActionStatus {
    OPEN = "OPEN",
    DONE = "DONE",
    CANCELED = "CANCELED"
}
enum BaseActionCategory {
    GENERAL = "GENERAL",
    EMAIL = "EMAIL",
    PHONE = "PHONE",
    SOCIAL = "SOCIAL",
    RECONNECT = "RECONNECT",
    FIRSTCALL = "FIRSTCALL",
    ANAMNESISCALL = "ANAMNESISCALL"
}
enum JourneyMydayCategory {
    KUNDENBETREUUNG_ONBOARDING = "KUNDENBETREUUNG_ONBOARDING",
    KUNDENBETREUUNG_SUCCESS = "KUNDENBETREUUNG_SUCCESS",
    KUNDENBETREUUNG_RECOVERY = "KUNDENBETREUUNG_RECOVERY",
    PARTNERBETREUUNG_ONBOARDING = "PARTNERBETREUUNG_ONBOARDING",
    PARTNERBETREUUNG_SUCCESS = "PARTNERBETREUUNG_SUCCESS",
    PARTNERBETREUUNG_RECOVERY = "PARTNERBETREUUNG_RECOVERY"
}
enum JourneyStatus {
    ACTIVE = "ACTIVE",
    DISABLED = "DISABLED"
}
enum TemplateActionCategory {
    GENERAL = "GENERAL",
    EMAIL = "EMAIL",
    PHONE = "PHONE",
    SOCIAL = "SOCIAL"
}
enum TemplateActionActionType {
    TASK = "TASK",
    AUTOMATION = "AUTOMATION"
}
enum TemplateActionAssignee {
    PARTNER = "PARTNER",
    COACH = "COACH"
}
enum AnamnesisFieldInputType {
    INFO = "INFO",
    STRING = "STRING",
    TEXT = "TEXT",
    NUMBER = "NUMBER",
    FILE = "FILE",
    BOOLEAN = "BOOLEAN",
    SELECT = "SELECT",
    MULTISELECT = "MULTISELECT",
    RADIO = "RADIO",
    DATE = "DATE"
}
enum Status {
    VISIBLE = "VISIBLE",
    HIDDEN = "HIDDEN"
}

type SMTPCredentialInput = {
    host: t.String;
    port: t.Int;
    password: t.String;
    secure: t.Boolean;
};
type CreateTemplateTaskInput = {
    subject: t.String;
    emailSubject?: t.String;
    note?: t.String;
    content?: t.String;
    rawContent?: t.String;
    category?: t.String;
    actionType: t.String;
    assignee: t.String;
    startDay?: t.Int;
    deadlineDay: t.Int;
    startDayWeekdays?: t.Int;
};
type UpdateTemplateTaskInput = {
    subject?: t.String;
    emailSubject?: t.String;
    note?: t.String;
    content?: t.String;
    rawContent?: t.String;
    category?: t.String;
    assignee?: t.String;
    startDay?: t.Int;
    deadlineDay?: t.Int;
    startDayWeekdays?: t.Int;
};
type ActionInput = {
    customerId?: t.ID;
    assigneeId?: t.ID;
    dueDate?: t.Date;
    subject?: t.String;
    emailSubject?: t.String;
    note?: t.String;
    content?: t.String;
    rawContent?: t.String;
    category?: t.String;
    status?: t.String;
};
type ContactInputType = {
    localId?: t.String;
    firstName?: t.String;
    lastName?: t.String;
    phoneNumber?: t.String;
    emailAddress?: t.String;
};
type CustomerInput = {
    unformattedTitlePrefix?: t.String;
    firstName?: t.String;
    lastName?: t.String;
    unformattedTitlePost?: t.String;
    birthdate?: t.Date;
    street1?: t.String;
    street2?: t.String;
    zipCode?: t.String;
    city?: t.String;
    country?: t.String;
    businessName?: t.String;
    businessStreet1?: t.String;
    businessStreet2?: t.String;
    businessZipCode?: t.String;
    businessCity?: t.String;
    businessCountry?: t.String;
    vatId?: t.String;
    vatStatus?: t.String;
    bankName?: t.String;
    bankIban?: t.String;
    bankBic?: t.String;
    email?: t.String;
    phone?: t.String;
    note?: t.String;
    isCustomer?: t.Boolean;
    gender?: t.String;
    changePartnerStatus?: t.Boolean;
    socialLinkFacebook?: t.String;
    socialLinkInstagram?: t.String;
    socialLinkTwitter?: t.String;
    socialLinkLinkedin?: t.String;
    socialLinkPinterest?: t.String;
    socialImage?: t.String;
    anamnesisData?: t.JSONString;
    beautycoach?: t.ID;
    partner?: t.ID;
    potentialPartner?: t.Boolean;
    potentialCustomer?: t.Boolean;
};
type ActivityInput = {
    title?: t.String;
    text?: t.String;
    hidden?: t.Boolean;
    private?: t.Boolean;
};
type AnamnesisDataInput = {
    slug: t.String;
    value: t.String;
    bigValue?: t.String;
};
type DocumentMetaInput = {
    documentType: t.String;
    questionTalkToCustomer?: t.String;
    questionPicturesOfCustomer?: t.String;
};
type DetailedUserInput = {
    id?: t.ID;
    firstName?: t.String;
    lastName?: t.String;
    microsoft?: t.JSONString;
    password?: t.String;
    oldPassword?: t.String;
    address?: AddressInput;
};
type AddressInput = {
    street1?: t.String;
    street2?: t.String;
    zipCode?: t.String;
    city?: t.String;
    country?: t.String;
};

export class Query {
    __typename: t.String;
    kpiDashboard: (args: {
        dateStart: t.Date;
        dateEnd: t.Date;
    }) => t.Nullable<DashboardType>;
    commissionCommissionRunReceiptArchive: (args: {
        id: t.ID;
    }) => t.Nullable<DocumentType>;
    commissionCommissionRun: (args: {
        id: t.ID;
    }) => t.Nullable<CommissionRunType>;
    commissionCommissionRuns: (args?: {
        monthly?: t.Boolean;
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => t.Nullable<CommissionRunConnection>;
    commissionCommissionDetail: (args: {
        id: t.ID;
    }) => t.Nullable<CommissionDetailType>;
    commissionCommissionDetails: (args?: {
        hideEmptyAmount?: t.Boolean;
        hideEmptyTotalEarned?: t.Boolean;
        partnerId?: t.ID;
        monthly?: t.Boolean;
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => t.Nullable<CommissionDetailConnection>;
    learningLearnings: t.Nullable<LearningType>[];
    contentlibEntriesByFolder: (args: {
        id: t.ID;
    }) => t.Nullable<ContentLibFolderType>;
    contentlibEntriesByRoot: t.Nullable<ContentLibFolderType>;
    contentlibEntry: (args: {
        id: t.ID;
    }) => t.Nullable<ContentLibraryEntryType>;
    contentlibEntries: (args?: {
        filterTitle?: t.String;
        filterKey?: t.String;
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => t.Nullable<ContentLibraryEntryTypeConnection>;
    contentlibSharingEntry: (args: {
        id: t.ID;
    }) => t.Nullable<InfoSharingEntryType>;
    contentlibSharingEntries: (args?: {
        filterTitle?: t.String;
        filterKey?: t.String;
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => t.Nullable<InfoSharingEntryTypeConnection>;
    journeyEntriesByFolder: (args: {
        id: t.ID;
    }) => t.Nullable<JourneyFolderType>;
    journeyEntriesByRoot: t.Nullable<JourneyFolderType>;
    journeyJourney: (args: {
        id: t.ID;
    }) => t.Nullable<JourneyType>;
    journeyJourneys: (args?: {
        filterStatus?: t.String;
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => t.Nullable<JourneyTypeConnection>;
    actionsAll: (args?: {
        filterStatus?: t.String;
        filterKey?: t.String;
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => t.Nullable<ActionTypeConnection>;
    actionsMyActions: (args?: {
        filterStatus?: t.String;
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => t.Nullable<ActionTypeConnection>;
    actionsByCustomer: (args: {
        customerId: t.ID;
        filterStatus?: t.String;
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => t.Nullable<ActionTypeConnection>;
    actionsMyMetrics: t.Nullable<ActionMetricType>;
    actionsAllMetrics: (args?: {
        filterKey?: t.String;
    }) => t.Nullable<ActionMetricType>;
    actionsDailyStorage: (args?: {
        motivation?: t.Int;
        regenerate?: t.Boolean;
        loadmore?: t.Boolean;
        all?: t.Boolean;
    }) => t.Nullable<DailyStorageType>;
    actionsDailyStorage2: (args?: {
        motivation?: t.Int;
        regenerate?: t.Boolean;
        loadmore?: t.Boolean;
        all?: t.Boolean;
    }) => t.Nullable<CategoryType>[];
    anamnesisCategories: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => t.Nullable<AnamnesisCategoryTypeConnection>;
    anamnesisFields: t.Nullable<AnamnesisFieldType>[];
    anamnesisOrdering: t.Nullable<AnamnesisOrderingType>;
    contactContact: (args: {
        id: t.ID;
    }) => t.Nullable<ContactType>;
    contactContacts: (args?: {
        filterKey?: t.String;
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => t.Nullable<ContactTypeConnection>;
    node: (args: {
        id: t.ID;
    }) => t.Nullable<Node>;
    customerCustomers: (args?: {
        filterKey?: t.String;
        orderBy?: t.String;
        isCustomer?: t.Boolean;
        isPartner?: t.Boolean;
        anamnesisFinishedOnce?: t.Boolean;
        anamnesisDraftOpened?: t.Boolean;
        daysSinceCreation?: t.Boolean;
        partner?: t.ID;
        noPartner?: t.Boolean;
        beautycoach?: t.ID;
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => t.Nullable<CustomerConnection>;
    customerCustomer: (args?: {
        id?: t.ID;
    }) => t.Nullable<CustomerType>;
    customerTags: t.Nullable<TagType>[];
    customerActivities: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
        category?: t.String;
    }) => t.Nullable<ActivityTypeConnection>;
    customerAnamnesisFieldHistory: (args: {
        id: t.ID;
    }) => t.Nullable<CustomerAnamnesisFieldHistoryType>;
    customerListByTag: (args?: {
        tag?: t.String;
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => t.Nullable<CustomerConnection>;
    userUsers: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => t.Nullable<UserTypeConnection>;
    userPartnerCoaches: (args?: {
        filterKey?: t.String;
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => t.Nullable<UserTypeConnection>;
    userCoaches: t.Nullable<UserType>[];
    userUser: (args: {
        id: t.Int;
    }) => t.Nullable<UserType>;
    userMe: t.Nullable<UserType>;
    userBanCodes: (args?: {
        code?: t.Int;
    }) => t.Nullable<BanCodeType>[];
    globalVersion: t.Nullable<t.String>;
    constructor() { this.__typename = ""; this.kpiDashboard = fnProxy(DashboardType); this.commissionCommissionRunReceiptArchive = fnProxy(DocumentType); this.commissionCommissionRun = fnProxy(CommissionRunType); this.commissionCommissionRuns = fnProxy(CommissionRunConnection); this.commissionCommissionDetail = fnProxy(CommissionDetailType); this.commissionCommissionDetails = fnProxy(CommissionDetailConnection); this.learningLearnings = arrayProxy(LearningType); this.contentlibEntriesByFolder = fnProxy(ContentLibFolderType); this.contentlibEntriesByRoot = proxy(ContentLibFolderType); this.contentlibEntry = fnProxy(ContentLibraryEntryType); this.contentlibEntries = fnProxy(ContentLibraryEntryTypeConnection); this.contentlibSharingEntry = fnProxy(InfoSharingEntryType); this.contentlibSharingEntries = fnProxy(InfoSharingEntryTypeConnection); this.journeyEntriesByFolder = fnProxy(JourneyFolderType); this.journeyEntriesByRoot = proxy(JourneyFolderType); this.journeyJourney = fnProxy(JourneyType); this.journeyJourneys = fnProxy(JourneyTypeConnection); this.actionsAll = fnProxy(ActionTypeConnection); this.actionsMyActions = fnProxy(ActionTypeConnection); this.actionsByCustomer = fnProxy(ActionTypeConnection); this.actionsMyMetrics = proxy(ActionMetricType); this.actionsAllMetrics = fnProxy(ActionMetricType); this.actionsDailyStorage = fnProxy(DailyStorageType); this.actionsDailyStorage2 = fnArrayProxy(CategoryType); this.anamnesisCategories = fnProxy(AnamnesisCategoryTypeConnection); this.anamnesisFields = arrayProxy(AnamnesisFieldType); this.anamnesisOrdering = proxy(AnamnesisOrderingType); this.contactContact = fnProxy(ContactType); this.contactContacts = fnProxy(ContactTypeConnection); this.node = fnProxy(Node); this.customerCustomers = fnProxy(CustomerConnection); this.customerCustomer = fnProxy(CustomerType); this.customerTags = arrayProxy(TagType); this.customerActivities = fnProxy(ActivityTypeConnection); this.customerAnamnesisFieldHistory = fnProxy(CustomerAnamnesisFieldHistoryType); this.customerListByTag = fnProxy(CustomerConnection); this.userUsers = fnProxy(UserTypeConnection); this.userPartnerCoaches = fnProxy(UserTypeConnection); this.userCoaches = arrayProxy(UserType); this.userUser = fnProxy(UserType); this.userMe = proxy(UserType); this.userBanCodes = fnArrayProxy(BanCodeType); this.globalVersion = null; }
}
export class DashboardType {
    __typename: t.String;
    dateStart: t.Nullable<t.Date>;
    dateEnd: t.Nullable<t.Date>;
    revenue: t.Nullable<DashboardItemType>;
    orderCount: t.Nullable<DashboardItemType>;
    averageOrderValue: t.Nullable<DashboardItemType>;
    constructor() { this.__typename = ""; this.dateStart = null; this.dateEnd = null; this.revenue = proxy(DashboardItemType); this.orderCount = proxy(DashboardItemType); this.averageOrderValue = proxy(DashboardItemType); }
}
export class DashboardItemType {
    __typename: t.String;
    total: t.Nullable<t.Float>;
    totalPrevious: t.Nullable<t.Float>;
    segments: t.Nullable<SegmentType>[];
    constructor() { this.__typename = ""; this.total = null; this.totalPrevious = null; this.segments = arrayProxy(SegmentType); }
}
export class SegmentType {
    __typename: t.String;
    dateStart: t.Nullable<t.DateTime>;
    dateEnd: t.Nullable<t.DateTime>;
    dateStartPrevious: t.Nullable<t.DateTime>;
    dateEndPrevious: t.Nullable<t.DateTime>;
    value: t.Nullable<t.Float>;
    valuePrevious: t.Nullable<t.Float>;
    constructor() { this.__typename = ""; this.dateStart = null; this.dateEnd = null; this.dateStartPrevious = null; this.dateEndPrevious = null; this.value = null; this.valuePrevious = null; }
}
export class DocumentType {
    __typename: t.String;
    id: t.ID;
    title: t.Nullable<t.String>;
    description: t.Nullable<t.String>;
    file: t.Nullable<t.String>;
    owner: UserType;
    createdAt: t.DateTime;
    slug: t.Nullable<t.String>;
    beautyreports: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => CharmDocumentTypeConnection;
    commissionRuns: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => CommissionRunTypeConnection;
    constructor() { this.__typename = ""; this.id = ""; this.title = null; this.description = null; this.file = null; this.owner = proxy(UserType); this.createdAt = ""; this.slug = null; this.beautyreports = fnProxy(CharmDocumentTypeConnection); this.commissionRuns = fnProxy(CommissionRunTypeConnection); }
}
export class UserType {
    __typename: t.String;
    id: t.ID;
    lastLogin: t.Nullable<t.DateTime>;
    utype: t.Int;
    bydesignCustomerId: t.Nullable<t.String>;
    bydesignPartnerId: t.Nullable<t.String>;
    isActive: t.Boolean;
    banReason: t.Nullable<t.Int>;
    createdAt: t.Nullable<t.DateTime>;
    lastLogoutAll: t.Nullable<t.DateTime>;
    firstName: t.Nullable<t.String>;
    lastName: t.Nullable<t.String>;
    titlePrefix: t.Nullable<t.String>;
    titlePost: t.Nullable<t.String>;
    gender: t.Nullable<UserGender>;
    emailAddress: t.Nullable<t.String>;
    tmpEmailAddress: t.Nullable<t.String>;
    phoneNumber: t.Nullable<t.String>;
    tmpPhoneNumber: t.Nullable<t.String>;
    street1: t.Nullable<t.String>;
    street2: t.Nullable<t.String>;
    zipCode: t.Nullable<t.String>;
    city: t.Nullable<t.String>;
    province: t.Nullable<t.String>;
    provinceCode: t.Nullable<t.String>;
    country: t.Nullable<t.String>;
    billStreet1: t.Nullable<t.String>;
    billStreet2: t.Nullable<t.String>;
    billZipCode: t.Nullable<t.String>;
    billCity: t.Nullable<t.String>;
    billProvince: t.Nullable<t.String>;
    billProvinceCode: t.Nullable<t.String>;
    billCountry: t.Nullable<t.String>;
    businessName: t.Nullable<t.String>;
    businessStreet1: t.Nullable<t.String>;
    businessStreet2: t.Nullable<t.String>;
    businessZipCode: t.Nullable<t.String>;
    businessCity: t.Nullable<t.String>;
    businessCountry: t.Nullable<t.String>;
    vatId: t.Nullable<t.String>;
    vatVerified: t.Nullable<UserVatVerified>;
    vatStatus: t.Nullable<t.String>;
    bankName: t.Nullable<t.String>;
    bankIban: t.Nullable<t.String>;
    bankBic: t.Nullable<t.String>;
    acceptsEmailMarketing: t.Boolean;
    acceptsSmsMarketing: t.Boolean;
    company: t.Nullable<t.String>;
    taxExempt: t.Boolean;
    lastPhoneRequest: t.Nullable<t.DateTime>;
    lastPhoneCodeRequest: t.Nullable<t.DateTime>;
    lastEmailRequest: t.Nullable<t.DateTime>;
    microsoft: t.Nullable<t.GenericScalar>;
    systemMessages: SystemMessageType[];
    documents: DocumentType[];
    customers: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => CustomerTypeConnection;
    customer: t.Nullable<CustomerType>;
    createdActivities: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
        category?: t.String;
    }) => ActivityTypeConnection;
    hiddenActivities: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
        category?: t.String;
    }) => ActivityTypeConnection;
    createdTags: TagType[];
    hiddenTags: TagType[];
    anamnesisHistories: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => CustomerAnamnesisFieldHistoryTypeConnection;
    contactSet: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => ContactTypeConnection;
    actionsAssigned: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => BaseActionTypeConnection;
    actionsCreated: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => BaseActionTypeConnection;
    actionsChanged: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => BaseActionTypeConnection;
    journeyFoldersCreated: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => JourneyFolderTypeConnection;
    journeyFoldersChanged: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => JourneyFolderTypeConnection;
    journeysCreated: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => JourneyTypeConnection;
    journeysChanged: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => JourneyTypeConnection;
    contentLibraryFoldersCreated: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => ContentLibFolderTypeConnection;
    contentLibraryFoldersChanged: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => ContentLibFolderTypeConnection;
    contentLibraryEntriesCreated: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
        filterTitle?: t.String;
        filterKey?: t.String;
    }) => ContentLibraryEntryTypeConnection;
    contentLibraryEntriesChanged: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
        filterTitle?: t.String;
        filterKey?: t.String;
    }) => ContentLibraryEntryTypeConnection;
    contentSharingEntriesCreated: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => InfoSharingEntryTypeConnection;
    contentSharingEntriesChanged: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => InfoSharingEntryTypeConnection;
    learningSet: LearningType[];
    learningallocationSet: LearningAllocationType[];
    isPartner: t.Nullable<t.Boolean>;
    isCustomer: t.Nullable<t.Boolean>;
    learningAllocations: t.Nullable<LearningAllocationType>[];
    userEmailConnection: t.Nullable<SnekEmailType>[];
    showMissingBusinessDataMessage: t.Nullable<t.Boolean>;
    constructor() { this.__typename = ""; this.id = ""; this.lastLogin = null; this.utype = 0; this.bydesignCustomerId = null; this.bydesignPartnerId = null; this.isActive = false; this.banReason = null; this.createdAt = null; this.lastLogoutAll = null; this.firstName = null; this.lastName = null; this.titlePrefix = null; this.titlePost = null; this.gender = null; this.emailAddress = null; this.tmpEmailAddress = null; this.phoneNumber = null; this.tmpPhoneNumber = null; this.street1 = null; this.street2 = null; this.zipCode = null; this.city = null; this.province = null; this.provinceCode = null; this.country = null; this.billStreet1 = null; this.billStreet2 = null; this.billZipCode = null; this.billCity = null; this.billProvince = null; this.billProvinceCode = null; this.billCountry = null; this.businessName = null; this.businessStreet1 = null; this.businessStreet2 = null; this.businessZipCode = null; this.businessCity = null; this.businessCountry = null; this.vatId = null; this.vatVerified = null; this.vatStatus = null; this.bankName = null; this.bankIban = null; this.bankBic = null; this.acceptsEmailMarketing = false; this.acceptsSmsMarketing = false; this.company = null; this.taxExempt = false; this.lastPhoneRequest = null; this.lastPhoneCodeRequest = null; this.lastEmailRequest = null; this.microsoft = null; this.systemMessages = arrayProxy(SystemMessageType); this.documents = arrayProxy(DocumentType); this.customers = fnProxy(CustomerTypeConnection); this.customer = proxy(CustomerType); this.createdActivities = fnProxy(ActivityTypeConnection); this.hiddenActivities = fnProxy(ActivityTypeConnection); this.createdTags = arrayProxy(TagType); this.hiddenTags = arrayProxy(TagType); this.anamnesisHistories = fnProxy(CustomerAnamnesisFieldHistoryTypeConnection); this.contactSet = fnProxy(ContactTypeConnection); this.actionsAssigned = fnProxy(BaseActionTypeConnection); this.actionsCreated = fnProxy(BaseActionTypeConnection); this.actionsChanged = fnProxy(BaseActionTypeConnection); this.journeyFoldersCreated = fnProxy(JourneyFolderTypeConnection); this.journeyFoldersChanged = fnProxy(JourneyFolderTypeConnection); this.journeysCreated = fnProxy(JourneyTypeConnection); this.journeysChanged = fnProxy(JourneyTypeConnection); this.contentLibraryFoldersCreated = fnProxy(ContentLibFolderTypeConnection); this.contentLibraryFoldersChanged = fnProxy(ContentLibFolderTypeConnection); this.contentLibraryEntriesCreated = fnProxy(ContentLibraryEntryTypeConnection); this.contentLibraryEntriesChanged = fnProxy(ContentLibraryEntryTypeConnection); this.contentSharingEntriesCreated = fnProxy(InfoSharingEntryTypeConnection); this.contentSharingEntriesChanged = fnProxy(InfoSharingEntryTypeConnection); this.learningSet = arrayProxy(LearningType); this.learningallocationSet = arrayProxy(LearningAllocationType); this.isPartner = null; this.isCustomer = null; this.learningAllocations = arrayProxy(LearningAllocationType); this.userEmailConnection = arrayProxy(SnekEmailType); this.showMissingBusinessDataMessage = null; }
}
export class Node {
    __typename: t.String;
    id: t.ID;
    constructor() { this.__typename = ""; this.id = ""; }
}
export class SystemMessageType {
    __typename: t.String;
    message: t.Nullable<t.JSONString>;
    code: t.Nullable<t.Int>;
    createdAt: t.DateTime;
    read: t.Boolean;
    readAt: t.Nullable<t.DateTime>;
    constructor() { this.__typename = ""; this.message = null; this.code = null; this.createdAt = ""; this.read = false; this.readAt = null; }
}
export class CustomerTypeConnection {
    __typename: t.String;
    pageInfo: PageInfo;
    edges: CustomerTypeEdge[];
    constructor() { this.__typename = ""; this.pageInfo = proxy(PageInfo); this.edges = arrayProxy(CustomerTypeEdge); }
}
export class PageInfo {
    __typename: t.String;
    hasNextPage: t.Boolean;
    hasPreviousPage: t.Boolean;
    startCursor: t.Nullable<t.String>;
    endCursor: t.Nullable<t.String>;
    constructor() { this.__typename = ""; this.hasNextPage = false; this.hasPreviousPage = false; this.startCursor = null; this.endCursor = null; }
}
export class CustomerTypeEdge {
    __typename: t.String;
    node: t.Nullable<CustomerType>;
    cursor: t.String;
    constructor() { this.__typename = ""; this.node = proxy(CustomerType); this.cursor = ""; }
}
export class CustomerType {
    __typename: t.String;
    id: t.ID;
    lastLogin: t.Nullable<t.DateTime>;
    utype: t.Int;
    bydesignCustomerId: t.Nullable<t.String>;
    bydesignPartnerId: t.Nullable<t.String>;
    isActive: t.Boolean;
    banReason: t.Nullable<t.Int>;
    createdAt: t.Nullable<t.DateTime>;
    lastLogoutAll: t.Nullable<t.DateTime>;
    firstName: t.Nullable<t.String>;
    lastName: t.Nullable<t.String>;
    titlePrefix: t.Nullable<t.String>;
    titlePost: t.Nullable<t.String>;
    gender: t.Nullable<t.String>;
    emailAddress: t.Nullable<t.String>;
    tmpEmailAddress: t.Nullable<t.String>;
    phoneNumber: t.Nullable<t.String>;
    tmpPhoneNumber: t.Nullable<t.String>;
    street1: t.Nullable<t.String>;
    street2: t.Nullable<t.String>;
    zipCode: t.Nullable<t.String>;
    city: t.Nullable<t.String>;
    province: t.Nullable<t.String>;
    provinceCode: t.Nullable<t.String>;
    country: t.Nullable<t.String>;
    billStreet1: t.Nullable<t.String>;
    billStreet2: t.Nullable<t.String>;
    billZipCode: t.Nullable<t.String>;
    billCity: t.Nullable<t.String>;
    billProvince: t.Nullable<t.String>;
    billProvinceCode: t.Nullable<t.String>;
    billCountry: t.Nullable<t.String>;
    businessName: t.Nullable<t.String>;
    businessStreet1: t.Nullable<t.String>;
    businessStreet2: t.Nullable<t.String>;
    businessZipCode: t.Nullable<t.String>;
    businessCity: t.Nullable<t.String>;
    businessCountry: t.Nullable<t.String>;
    vatId: t.Nullable<t.String>;
    vatVerified: t.Nullable<UserVatVerified>;
    vatStatus: t.Nullable<t.String>;
    bankName: t.Nullable<t.String>;
    bankIban: t.Nullable<t.String>;
    bankBic: t.Nullable<t.String>;
    acceptsEmailMarketing: t.Boolean;
    acceptsSmsMarketing: t.Boolean;
    company: t.Nullable<t.String>;
    taxExempt: t.Boolean;
    lastPhoneRequest: t.Nullable<t.DateTime>;
    lastPhoneCodeRequest: t.Nullable<t.DateTime>;
    lastEmailRequest: t.Nullable<t.DateTime>;
    microsoft: t.Nullable<t.JSONString>;
    password: t.String;
    username: t.String;
    isAdmin: t.Boolean;
    defaultSuperuser: t.Boolean;
    birthdate: t.Nullable<t.Date>;
    note: t.Nullable<t.String>;
    isCustomer: t.Boolean;
    isPartner: t.Boolean;
    lastChanged: t.DateTime;
    isVerified: t.Boolean;
    anamnesisDraftOpened: t.Boolean;
    anamnesisFinishedOnce: t.Boolean;
    anamnesisFinishedAt: t.Nullable<t.DateTime>;
    socialLinkFacebook: t.Nullable<t.String>;
    socialLinkInstagram: t.Nullable<t.String>;
    socialLinkTwitter: t.Nullable<t.String>;
    socialLinkLinkedin: t.Nullable<t.String>;
    socialLinkPinterest: t.Nullable<t.String>;
    potentialPartner: t.Boolean;
    potentialCustomer: t.Boolean;
    socialImage: t.Nullable<CustomerSocialImage>;
    beautycoach: t.Nullable<UserType>;
    partner: t.Nullable<CustomerType>;
    partnerCustomers: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => CustomerTypeConnection;
    activities: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
        category?: t.String;
    }) => ActivityTypeConnection;
    tags: TagReferenceType[];
    anamnesis: CustomerAnamnesisFieldType[];
    beautyreports: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => CharmDocumentTypeConnection;
    orders: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => OrderTypeConnection;
    actions: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => BaseActionTypeConnection;
    dailyTaskStorage: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => DailyStorageTypeConnection;
    journeys: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => JourneyUserReferenceTypeConnection;
    commissionDetails: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => CommissionDetailTypeConnection;
    email: t.Nullable<t.String>;
    phone: t.Nullable<t.String>;
    anamnesisAllRequired: t.Nullable<t.Boolean>;
    anamnesisProgress: t.Nullable<AnamnesisProgressType>;
    anamnesisProgressByCategory: (args?: {
        categoryId?: t.ID;
    }) => t.Nullable<AnamnesisProgressType>;
    anamnesisProgressPerCategory: t.Nullable<AnamnesisProgressType>[];
    unformattedTitlePrefix: t.Nullable<t.String>;
    unformattedTitlePost: t.Nullable<t.String>;
    orderStats: (args?: {
        startDate?: t.Date;
        endDate?: t.Date;
    }) => t.Nullable<OrderStatsType>;
    orderStatsBackup: (args?: {
        startDate?: t.Date;
        endDate?: t.Date;
    }) => t.Nullable<OrderStatsType>;
    learningAllocations: t.Nullable<LearningAllocationType>[];
    learningSet: t.Nullable<LearningType>[];
    wishlist: t.Nullable<WishlistProductType>[];
    constructor() { this.__typename = ""; this.id = ""; this.lastLogin = null; this.utype = 0; this.bydesignCustomerId = null; this.bydesignPartnerId = null; this.isActive = false; this.banReason = null; this.createdAt = null; this.lastLogoutAll = null; this.firstName = null; this.lastName = null; this.titlePrefix = null; this.titlePost = null; this.gender = null; this.emailAddress = null; this.tmpEmailAddress = null; this.phoneNumber = null; this.tmpPhoneNumber = null; this.street1 = null; this.street2 = null; this.zipCode = null; this.city = null; this.province = null; this.provinceCode = null; this.country = null; this.billStreet1 = null; this.billStreet2 = null; this.billZipCode = null; this.billCity = null; this.billProvince = null; this.billProvinceCode = null; this.billCountry = null; this.businessName = null; this.businessStreet1 = null; this.businessStreet2 = null; this.businessZipCode = null; this.businessCity = null; this.businessCountry = null; this.vatId = null; this.vatVerified = null; this.vatStatus = null; this.bankName = null; this.bankIban = null; this.bankBic = null; this.acceptsEmailMarketing = false; this.acceptsSmsMarketing = false; this.company = null; this.taxExempt = false; this.lastPhoneRequest = null; this.lastPhoneCodeRequest = null; this.lastEmailRequest = null; this.microsoft = null; this.password = ""; this.username = ""; this.isAdmin = false; this.defaultSuperuser = false; this.birthdate = null; this.note = null; this.isCustomer = false; this.isPartner = false; this.lastChanged = ""; this.isVerified = false; this.anamnesisDraftOpened = false; this.anamnesisFinishedOnce = false; this.anamnesisFinishedAt = null; this.socialLinkFacebook = null; this.socialLinkInstagram = null; this.socialLinkTwitter = null; this.socialLinkLinkedin = null; this.socialLinkPinterest = null; this.potentialPartner = false; this.potentialCustomer = false; this.socialImage = null; this.beautycoach = proxy(UserType); this.partner = proxy(CustomerType); this.partnerCustomers = fnProxy(CustomerTypeConnection); this.activities = fnProxy(ActivityTypeConnection); this.tags = arrayProxy(TagReferenceType); this.anamnesis = arrayProxy(CustomerAnamnesisFieldType); this.beautyreports = fnProxy(CharmDocumentTypeConnection); this.orders = fnProxy(OrderTypeConnection); this.actions = fnProxy(BaseActionTypeConnection); this.dailyTaskStorage = fnProxy(DailyStorageTypeConnection); this.journeys = fnProxy(JourneyUserReferenceTypeConnection); this.commissionDetails = fnProxy(CommissionDetailTypeConnection); this.email = null; this.phone = null; this.anamnesisAllRequired = null; this.anamnesisProgress = proxy(AnamnesisProgressType); this.anamnesisProgressByCategory = fnProxy(AnamnesisProgressType); this.anamnesisProgressPerCategory = arrayProxy(AnamnesisProgressType); this.unformattedTitlePrefix = null; this.unformattedTitlePost = null; this.orderStats = fnProxy(OrderStatsType); this.orderStatsBackup = fnProxy(OrderStatsType); this.learningAllocations = arrayProxy(LearningAllocationType); this.learningSet = arrayProxy(LearningType); this.wishlist = arrayProxy(WishlistProductType); }
}
export class ActivityTypeConnection {
    __typename: t.String;
    pageInfo: PageInfo;
    edges: ActivityTypeEdge[];
    constructor() { this.__typename = ""; this.pageInfo = proxy(PageInfo); this.edges = arrayProxy(ActivityTypeEdge); }
}
export class ActivityTypeEdge {
    __typename: t.String;
    node: t.Nullable<ActivityType>;
    cursor: t.String;
    constructor() { this.__typename = ""; this.node = proxy(ActivityType); this.cursor = ""; }
}
export class ActivityType {
    __typename: t.String;
    id: t.ID;
    customer: CustomerType;
    category: t.Nullable<t.String>;
    title: t.Nullable<t.String>;
    text: t.Nullable<t.String>;
    datetime: t.Nullable<t.DateTime>;
    private: t.Nullable<t.Boolean>;
    createdBy: t.Nullable<UserType>;
    constructor() { this.__typename = ""; this.id = ""; this.customer = proxy(CustomerType); this.category = null; this.title = null; this.text = null; this.datetime = null; this.private = null; this.createdBy = proxy(UserType); }
}
export class TagReferenceType {
    __typename: t.String;
    tag: t.Nullable<t.String>;
    createdAt: t.Nullable<t.DateTime>;
    constructor() { this.__typename = ""; this.tag = null; this.createdAt = null; }
}
export class CustomerAnamnesisFieldType {
    __typename: t.String;
    slug: t.Nullable<t.String>;
    history: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => CustomerAnamnesisFieldHistoryTypeConnection;
    constructor() { this.__typename = ""; this.slug = null; this.history = fnProxy(CustomerAnamnesisFieldHistoryTypeConnection); }
}
export class CustomerAnamnesisFieldHistoryTypeConnection {
    __typename: t.String;
    pageInfo: PageInfo;
    edges: CustomerAnamnesisFieldHistoryTypeEdge[];
    constructor() { this.__typename = ""; this.pageInfo = proxy(PageInfo); this.edges = arrayProxy(CustomerAnamnesisFieldHistoryTypeEdge); }
}
export class CustomerAnamnesisFieldHistoryTypeEdge {
    __typename: t.String;
    node: t.Nullable<CustomerAnamnesisFieldHistoryType>;
    cursor: t.String;
    constructor() { this.__typename = ""; this.node = proxy(CustomerAnamnesisFieldHistoryType); this.cursor = ""; }
}
export class CustomerAnamnesisFieldHistoryType {
    __typename: t.String;
    id: t.ID;
    datetime: t.Nullable<t.DateTime>;
    value: t.Nullable<t.GenericScalar>;
    bigValue: t.Nullable<t.String>;
    changedBy: t.Nullable<UserType>;
    name: t.Nullable<t.String>;
    inputType: t.Nullable<t.String>;
    options: t.Nullable<t.GenericScalar>;
    constructor() { this.__typename = ""; this.id = ""; this.datetime = null; this.value = null; this.bigValue = null; this.changedBy = proxy(UserType); this.name = null; this.inputType = null; this.options = null; }
}
export class CharmDocumentTypeConnection {
    __typename: t.String;
    pageInfo: PageInfo;
    edges: CharmDocumentTypeEdge[];
    constructor() { this.__typename = ""; this.pageInfo = proxy(PageInfo); this.edges = arrayProxy(CharmDocumentTypeEdge); }
}
export class CharmDocumentTypeEdge {
    __typename: t.String;
    node: t.Nullable<CharmDocumentType>;
    cursor: t.String;
    constructor() { this.__typename = ""; this.node = proxy(CharmDocumentType); this.cursor = ""; }
}
export class CharmDocumentType {
    __typename: t.String;
    id: t.ID;
    datetime: t.Nullable<t.DateTime>;
    title: t.Nullable<t.String>;
    documentType: t.Nullable<CharmDocumentDocumentType>;
    document: t.Nullable<DocumentType>;
    hidden: t.Boolean;
    constructor() { this.__typename = ""; this.id = ""; this.datetime = null; this.title = null; this.documentType = null; this.document = proxy(DocumentType); this.hidden = false; }
}
export class OrderTypeConnection {
    __typename: t.String;
    pageInfo: PageInfo;
    edges: OrderTypeEdge[];
    constructor() { this.__typename = ""; this.pageInfo = proxy(PageInfo); this.edges = arrayProxy(OrderTypeEdge); }
}
export class OrderTypeEdge {
    __typename: t.String;
    node: t.Nullable<OrderType>;
    cursor: t.String;
    constructor() { this.__typename = ""; this.node = proxy(OrderType); this.cursor = ""; }
}
export class OrderType {
    __typename: t.String;
    id: t.ID;
    customer: t.Nullable<CustomerType>;
    bydesignId: t.Nullable<t.Int>;
    myfactoryId: t.Nullable<t.Int>;
    myfactoryError: t.Nullable<t.String>;
    status: t.Nullable<t.String>;
    billName1: t.Nullable<t.String>;
    billName2: t.Nullable<t.String>;
    billStreet1: t.Nullable<t.String>;
    billStreet2: t.Nullable<t.String>;
    billCity: t.Nullable<t.String>;
    billState: t.Nullable<t.String>;
    billPostalCode: t.Nullable<t.String>;
    billCountry: t.Nullable<t.String>;
    billEmail: t.Nullable<t.String>;
    billPhone: t.Nullable<t.String>;
    shipName1: t.Nullable<t.String>;
    shipName2: t.Nullable<t.String>;
    shipStreet1: t.Nullable<t.String>;
    shipStreet2: t.Nullable<t.String>;
    shipCity: t.Nullable<t.String>;
    shipState: t.Nullable<t.String>;
    shipPostalCode: t.Nullable<t.String>;
    shipCountry: t.Nullable<t.String>;
    shipEmail: t.Nullable<t.String>;
    shipPhone: t.Nullable<t.String>;
    shipGeoCode: t.Nullable<t.String>;
    invoiceNotes: t.Nullable<t.String>;
    shipMethodId: t.Nullable<t.Int>;
    shipMethod: t.Nullable<t.String>;
    market: t.Nullable<t.String>;
    rankPriceType: t.Nullable<t.String>;
    partyId: t.Nullable<t.Int>;
    currencyTypeId: t.Nullable<t.Int>;
    isGiftOrder: t.Boolean;
    handlingFee: t.Nullable<t.Float>;
    handlingTax: t.Nullable<t.Float>;
    shippingTotal: t.Nullable<t.Float>;
    shippingTax: t.Nullable<t.Float>;
    bonusDate: t.Nullable<t.DateTime>;
    compPeriodId: t.Nullable<t.Int>;
    isHostOrder: t.Boolean;
    isOverrideHandlingFee: t.Boolean;
    isOverrideShipping: t.Boolean;
    isTaxExempt: t.Boolean;
    packslipSupressFinancials: t.Boolean;
    createdBy: t.Nullable<t.String>;
    dateCreated: t.Nullable<t.DateTime>;
    orderDate: t.Nullable<t.DateTime>;
    datePosted: t.Nullable<t.DateTime>;
    dateShipped: t.Nullable<t.DateTime>;
    trackingId: t.Nullable<t.String>;
    lastModifiedBy: t.Nullable<t.String>;
    dateLastModified: t.Nullable<t.DateTime>;
    statusId: t.Nullable<t.Int>;
    subjectId: t.Nullable<t.Int>;
    subjectTypeId: t.Nullable<t.Int>;
    secondarySubjectId: t.Nullable<t.Int>;
    secondarySubjectTypeId: t.Nullable<t.Int>;
    rankPriceTypeId: t.Nullable<t.Int>;
    retryPaymentRuleId: t.Nullable<t.Int>;
    marketShowId: t.Nullable<t.Int>;
    multiplierTypeId: t.Nullable<t.Int>;
    periodId: t.Nullable<t.Int>;
    orderItems: OrderItemType[];
    taxTotal: t.Nullable<t.Float>;
    totalPrice: t.Nullable<t.Float>;
    constructor() { this.__typename = ""; this.id = ""; this.customer = proxy(CustomerType); this.bydesignId = null; this.myfactoryId = null; this.myfactoryError = null; this.status = null; this.billName1 = null; this.billName2 = null; this.billStreet1 = null; this.billStreet2 = null; this.billCity = null; this.billState = null; this.billPostalCode = null; this.billCountry = null; this.billEmail = null; this.billPhone = null; this.shipName1 = null; this.shipName2 = null; this.shipStreet1 = null; this.shipStreet2 = null; this.shipCity = null; this.shipState = null; this.shipPostalCode = null; this.shipCountry = null; this.shipEmail = null; this.shipPhone = null; this.shipGeoCode = null; this.invoiceNotes = null; this.shipMethodId = null; this.shipMethod = null; this.market = null; this.rankPriceType = null; this.partyId = null; this.currencyTypeId = null; this.isGiftOrder = false; this.handlingFee = null; this.handlingTax = null; this.shippingTotal = null; this.shippingTax = null; this.bonusDate = null; this.compPeriodId = null; this.isHostOrder = false; this.isOverrideHandlingFee = false; this.isOverrideShipping = false; this.isTaxExempt = false; this.packslipSupressFinancials = false; this.createdBy = null; this.dateCreated = null; this.orderDate = null; this.datePosted = null; this.dateShipped = null; this.trackingId = null; this.lastModifiedBy = null; this.dateLastModified = null; this.statusId = null; this.subjectId = null; this.subjectTypeId = null; this.secondarySubjectId = null; this.secondarySubjectTypeId = null; this.rankPriceTypeId = null; this.retryPaymentRuleId = null; this.marketShowId = null; this.multiplierTypeId = null; this.periodId = null; this.orderItems = arrayProxy(OrderItemType); this.taxTotal = null; this.totalPrice = null; }
}
export class OrderItemType {
    __typename: t.String;
    id: t.ID;
    order: OrderType;
    orderDetailId: t.Nullable<t.Int>;
    productId: t.Nullable<t.String>;
    description: t.Nullable<t.String>;
    quantity: t.Nullable<t.Int>;
    price: t.Nullable<t.Float>;
    volume1: t.Nullable<t.Float>;
    volume2: t.Nullable<t.Float>;
    volume3: t.Nullable<t.Float>;
    volume4: t.Nullable<t.Float>;
    otherPrice1: t.Nullable<t.Float>;
    otherPrice2: t.Nullable<t.Float>;
    otherPrice3: t.Nullable<t.Float>;
    otherPrice4: t.Nullable<t.Float>;
    tax: t.Nullable<t.Float>;
    taxableAmount: t.Nullable<t.Float>;
    isGroupOwner: t.Boolean;
    parentOrderDetailId: t.Nullable<t.Int>;
    warehouseName: t.Nullable<t.String>;
    warehouseCompanyTaxId: t.Nullable<t.String>;
    packslipProcessId: t.Nullable<t.Int>;
    packslipDescription: t.Nullable<t.String>;
    compareMsrp: t.Nullable<t.Float>;
    weight: t.Nullable<t.Float>;
    minResellablePrice: t.Nullable<t.Float>;
    constructor() { this.__typename = ""; this.id = ""; this.order = proxy(OrderType); this.orderDetailId = null; this.productId = null; this.description = null; this.quantity = null; this.price = null; this.volume1 = null; this.volume2 = null; this.volume3 = null; this.volume4 = null; this.otherPrice1 = null; this.otherPrice2 = null; this.otherPrice3 = null; this.otherPrice4 = null; this.tax = null; this.taxableAmount = null; this.isGroupOwner = false; this.parentOrderDetailId = null; this.warehouseName = null; this.warehouseCompanyTaxId = null; this.packslipProcessId = null; this.packslipDescription = null; this.compareMsrp = null; this.weight = null; this.minResellablePrice = null; }
}
export class BaseActionTypeConnection {
    __typename: t.String;
    pageInfo: PageInfo;
    edges: BaseActionTypeEdge[];
    constructor() { this.__typename = ""; this.pageInfo = proxy(PageInfo); this.edges = arrayProxy(BaseActionTypeEdge); }
}
export class BaseActionTypeEdge {
    __typename: t.String;
    node: t.Nullable<BaseActionType>;
    cursor: t.String;
    constructor() { this.__typename = ""; this.node = proxy(BaseActionType); this.cursor = ""; }
}
export class BaseActionType {
    __typename: t.String;
    id: t.ID;
    customer: t.Nullable<CustomerType>;
    contact: t.Nullable<ContactType>;
    assignee: t.Nullable<UserType>;
    dueDate: t.Nullable<t.Date>;
    deadlineDate: t.Nullable<t.Date>;
    startDate: t.Nullable<t.Date>;
    subject: t.Nullable<t.String>;
    emailSubject: t.Nullable<t.String>;
    note: t.Nullable<t.String>;
    content: t.Nullable<t.String>;
    rawContent: t.Nullable<t.String>;
    status: t.Nullable<BaseActionStatus>;
    rescheduledAt: t.Nullable<t.DateTime>;
    rescheduledDays: t.Nullable<t.Int>;
    rescheduledFrom: t.Nullable<BaseActionType>;
    category: t.Nullable<BaseActionCategory>;
    createdBy: t.Nullable<UserType>;
    createdAt: t.DateTime;
    completedAt: t.Nullable<t.DateTime>;
    actionType: t.Nullable<t.String>;
    journeyUserReference: t.Nullable<JourneyUserReferenceType>;
    templateActionReference: t.Nullable<TemplateActionType>;
    rescheduledTo: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => BaseActionTypeConnection;
    action: t.Nullable<ActionType>;
    dailyaction: t.Nullable<DailyActionType>;
    journey: t.Nullable<JourneyType>;
    journeyTask: t.Nullable<t.Boolean>;
    translatedContent: t.Nullable<t.String>;
    translatedRawContent: t.Nullable<t.String>;
    rescheduledToDate: t.Nullable<t.Date>;
    rescheduledFromDate: t.Nullable<t.Date>;
    constructor() { this.__typename = ""; this.id = ""; this.customer = proxy(CustomerType); this.contact = proxy(ContactType); this.assignee = proxy(UserType); this.dueDate = null; this.deadlineDate = null; this.startDate = null; this.subject = null; this.emailSubject = null; this.note = null; this.content = null; this.rawContent = null; this.status = null; this.rescheduledAt = null; this.rescheduledDays = null; this.rescheduledFrom = proxy(BaseActionType); this.category = null; this.createdBy = proxy(UserType); this.createdAt = ""; this.completedAt = null; this.actionType = null; this.journeyUserReference = proxy(JourneyUserReferenceType); this.templateActionReference = proxy(TemplateActionType); this.rescheduledTo = fnProxy(BaseActionTypeConnection); this.action = proxy(ActionType); this.dailyaction = proxy(DailyActionType); this.journey = proxy(JourneyType); this.journeyTask = null; this.translatedContent = null; this.translatedRawContent = null; this.rescheduledToDate = null; this.rescheduledFromDate = null; }
}
export class ContactType {
    __typename: t.String;
    localId: t.Nullable<t.String>;
    firstName: t.Nullable<t.String>;
    lastName: t.Nullable<t.String>;
    phoneNumber: t.Nullable<t.String>;
    emailAddress: t.Nullable<t.String>;
    user: t.Nullable<UserType>;
    createdAt: t.DateTime;
    lastChanged: t.DateTime;
    id: t.ID;
    constructor() { this.__typename = ""; this.localId = null; this.firstName = null; this.lastName = null; this.phoneNumber = null; this.emailAddress = null; this.user = proxy(UserType); this.createdAt = ""; this.lastChanged = ""; this.id = ""; }
}
export class JourneyUserReferenceType {
    __typename: t.String;
    id: t.ID;
    journey: JourneyType;
    customer: t.Nullable<CustomerType>;
    joinDate: t.Date;
    currentBaseDate: t.Date;
    removedAt: t.Nullable<t.DateTime>;
    finishedAt: t.Nullable<t.DateTime>;
    actions: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => BaseActionTypeConnection;
    isFinished: t.Nullable<t.Boolean>;
    currentStep: t.Nullable<t.Int>;
    stepCount: t.Nullable<t.Int>;
    constructor() { this.__typename = ""; this.id = ""; this.journey = proxy(JourneyType); this.customer = proxy(CustomerType); this.joinDate = ""; this.currentBaseDate = ""; this.removedAt = null; this.finishedAt = null; this.actions = fnProxy(BaseActionTypeConnection); this.isFinished = null; this.currentStep = null; this.stepCount = null; }
}
export class JourneyType {
    __typename: t.String;
    id: t.ID;
    title: t.Nullable<t.String>;
    description: t.Nullable<t.String>;
    mydayCategory: t.Nullable<JourneyMydayCategory>;
    status: t.Nullable<JourneyStatus>;
    folder: t.Nullable<JourneyFolderType>;
    createdBy: t.Nullable<UserType>;
    createdAt: t.DateTime;
    changedBy: t.Nullable<UserType>;
    changedAt: t.DateTime;
    templateActions: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => TemplateActionTypeConnection;
    customers: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => JourneyUserReferenceTypeConnection;
    assignedCount: t.Nullable<t.Int>;
    finishedCount: t.Nullable<t.Int>;
    constructor() { this.__typename = ""; this.id = ""; this.title = null; this.description = null; this.mydayCategory = null; this.status = null; this.folder = proxy(JourneyFolderType); this.createdBy = proxy(UserType); this.createdAt = ""; this.changedBy = proxy(UserType); this.changedAt = ""; this.templateActions = fnProxy(TemplateActionTypeConnection); this.customers = fnProxy(JourneyUserReferenceTypeConnection); this.assignedCount = null; this.finishedCount = null; }
}
export class JourneyFolderType {
    __typename: t.String;
    id: t.ID;
    title: t.Nullable<t.String>;
    parent: t.Nullable<JourneyFolderType>;
    createdBy: t.Nullable<UserType>;
    createdAt: t.DateTime;
    changedBy: t.Nullable<UserType>;
    changedAt: t.DateTime;
    children: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => JourneyFolderTypeConnection;
    journeys: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => JourneyTypeConnection;
    countJourneys: t.Nullable<t.Int>;
    countChildrenFolders: t.Nullable<t.Int>;
    constructor() { this.__typename = ""; this.id = ""; this.title = null; this.parent = proxy(JourneyFolderType); this.createdBy = proxy(UserType); this.createdAt = ""; this.changedBy = proxy(UserType); this.changedAt = ""; this.children = fnProxy(JourneyFolderTypeConnection); this.journeys = fnProxy(JourneyTypeConnection); this.countJourneys = null; this.countChildrenFolders = null; }
}
export class JourneyFolderTypeConnection {
    __typename: t.String;
    pageInfo: PageInfo;
    edges: JourneyFolderTypeEdge[];
    constructor() { this.__typename = ""; this.pageInfo = proxy(PageInfo); this.edges = arrayProxy(JourneyFolderTypeEdge); }
}
export class JourneyFolderTypeEdge {
    __typename: t.String;
    node: t.Nullable<JourneyFolderType>;
    cursor: t.String;
    constructor() { this.__typename = ""; this.node = proxy(JourneyFolderType); this.cursor = ""; }
}
export class JourneyTypeConnection {
    __typename: t.String;
    pageInfo: PageInfo;
    edges: JourneyTypeEdge[];
    constructor() { this.__typename = ""; this.pageInfo = proxy(PageInfo); this.edges = arrayProxy(JourneyTypeEdge); }
}
export class JourneyTypeEdge {
    __typename: t.String;
    node: t.Nullable<JourneyType>;
    cursor: t.String;
    constructor() { this.__typename = ""; this.node = proxy(JourneyType); this.cursor = ""; }
}
export class TemplateActionTypeConnection {
    __typename: t.String;
    pageInfo: PageInfo;
    edges: TemplateActionTypeEdge[];
    constructor() { this.__typename = ""; this.pageInfo = proxy(PageInfo); this.edges = arrayProxy(TemplateActionTypeEdge); }
}
export class TemplateActionTypeEdge {
    __typename: t.String;
    node: t.Nullable<TemplateActionType>;
    cursor: t.String;
    constructor() { this.__typename = ""; this.node = proxy(TemplateActionType); this.cursor = ""; }
}
export class TemplateActionType {
    __typename: t.String;
    id: t.ID;
    subject: t.Nullable<t.String>;
    emailSubject: t.Nullable<t.String>;
    note: t.Nullable<t.String>;
    content: t.Nullable<t.String>;
    rawContent: t.Nullable<t.String>;
    category: t.Nullable<TemplateActionCategory>;
    actionType: t.Nullable<TemplateActionActionType>;
    assignee: t.Nullable<TemplateActionAssignee>;
    startDay: t.Nullable<t.Int>;
    deadlineDay: t.Nullable<t.Int>;
    startDayWeekdays: t.Nullable<t.Int>[];
    journey: JourneyType;
    actions: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => BaseActionTypeConnection;
    positionIndex: t.Nullable<t.Int>;
    originalId: t.Nullable<t.ID>;
    constructor() { this.__typename = ""; this.id = ""; this.subject = null; this.emailSubject = null; this.note = null; this.content = null; this.rawContent = null; this.category = null; this.actionType = null; this.assignee = null; this.startDay = null; this.deadlineDay = null; this.startDayWeekdays = []; this.journey = proxy(JourneyType); this.actions = fnProxy(BaseActionTypeConnection); this.positionIndex = null; this.originalId = null; }
}
export class JourneyUserReferenceTypeConnection {
    __typename: t.String;
    pageInfo: PageInfo;
    edges: JourneyUserReferenceTypeEdge[];
    constructor() { this.__typename = ""; this.pageInfo = proxy(PageInfo); this.edges = arrayProxy(JourneyUserReferenceTypeEdge); }
}
export class JourneyUserReferenceTypeEdge {
    __typename: t.String;
    node: t.Nullable<JourneyUserReferenceType>;
    cursor: t.String;
    constructor() { this.__typename = ""; this.node = proxy(JourneyUserReferenceType); this.cursor = ""; }
}
export class ActionType {
    __typename: t.String;
    id: t.ID;
    customer: t.Nullable<CustomerType>;
    contact: t.Nullable<ContactType>;
    assignee: t.Nullable<UserType>;
    dueDate: t.Nullable<t.Date>;
    deadlineDate: t.Nullable<t.Date>;
    startDate: t.Nullable<t.Date>;
    subject: t.Nullable<t.String>;
    emailSubject: t.Nullable<t.String>;
    note: t.Nullable<t.String>;
    content: t.Nullable<t.String>;
    rawContent: t.Nullable<t.String>;
    status: t.Nullable<BaseActionStatus>;
    rescheduledAt: t.Nullable<t.DateTime>;
    rescheduledDays: t.Nullable<t.Int>;
    rescheduledFrom: t.Nullable<BaseActionType>;
    category: t.Nullable<BaseActionCategory>;
    createdBy: t.Nullable<UserType>;
    createdAt: t.DateTime;
    completedAt: t.Nullable<t.DateTime>;
    actionType: t.Nullable<t.String>;
    journeyUserReference: t.Nullable<JourneyUserReferenceType>;
    templateActionReference: t.Nullable<TemplateActionType>;
    baseactionPtr: BaseActionType;
    dailyDefaultTask: ActionCollectionType[];
    journey: t.Nullable<JourneyType>;
    journeyTask: t.Nullable<t.Boolean>;
    translatedContent: t.Nullable<t.String>;
    translatedRawContent: t.Nullable<t.String>;
    rescheduledToDate: t.Nullable<t.Date>;
    rescheduledFromDate: t.Nullable<t.Date>;
    constructor() { this.__typename = ""; this.id = ""; this.customer = proxy(CustomerType); this.contact = proxy(ContactType); this.assignee = proxy(UserType); this.dueDate = null; this.deadlineDate = null; this.startDate = null; this.subject = null; this.emailSubject = null; this.note = null; this.content = null; this.rawContent = null; this.status = null; this.rescheduledAt = null; this.rescheduledDays = null; this.rescheduledFrom = proxy(BaseActionType); this.category = null; this.createdBy = proxy(UserType); this.createdAt = ""; this.completedAt = null; this.actionType = null; this.journeyUserReference = proxy(JourneyUserReferenceType); this.templateActionReference = proxy(TemplateActionType); this.baseactionPtr = proxy(BaseActionType); this.dailyDefaultTask = arrayProxy(ActionCollectionType); this.journey = proxy(JourneyType); this.journeyTask = null; this.translatedContent = null; this.translatedRawContent = null; this.rescheduledToDate = null; this.rescheduledFromDate = null; }
}
export class ActionCollectionType {
    __typename: t.String;
    generalTasks: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => ActionTypeConnection;
    reconnects: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => DailyActionTypeConnection;
    firstCalls: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => DailyActionTypeConnection;
    anamnesisCalls: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => DailyActionTypeConnection;
    swipes: t.Int;
    dailyTaskStorage: t.Nullable<DailyStorageType>;
    constructor() { this.__typename = ""; this.generalTasks = fnProxy(ActionTypeConnection); this.reconnects = fnProxy(DailyActionTypeConnection); this.firstCalls = fnProxy(DailyActionTypeConnection); this.anamnesisCalls = fnProxy(DailyActionTypeConnection); this.swipes = 0; this.dailyTaskStorage = proxy(DailyStorageType); }
}
export class ActionTypeConnection {
    __typename: t.String;
    pageInfo: PageInfo;
    edges: ActionTypeEdge[];
    constructor() { this.__typename = ""; this.pageInfo = proxy(PageInfo); this.edges = arrayProxy(ActionTypeEdge); }
}
export class ActionTypeEdge {
    __typename: t.String;
    node: t.Nullable<ActionType>;
    cursor: t.String;
    constructor() { this.__typename = ""; this.node = proxy(ActionType); this.cursor = ""; }
}
export class DailyActionTypeConnection {
    __typename: t.String;
    pageInfo: PageInfo;
    edges: DailyActionTypeEdge[];
    constructor() { this.__typename = ""; this.pageInfo = proxy(PageInfo); this.edges = arrayProxy(DailyActionTypeEdge); }
}
export class DailyActionTypeEdge {
    __typename: t.String;
    node: t.Nullable<DailyActionType>;
    cursor: t.String;
    constructor() { this.__typename = ""; this.node = proxy(DailyActionType); this.cursor = ""; }
}
export class DailyActionType {
    __typename: t.String;
    id: t.ID;
    customer: t.Nullable<CustomerType>;
    contact: t.Nullable<ContactType>;
    assignee: t.Nullable<UserType>;
    dueDate: t.Nullable<t.Date>;
    deadlineDate: t.Nullable<t.Date>;
    startDate: t.Nullable<t.Date>;
    subject: t.Nullable<t.String>;
    emailSubject: t.Nullable<t.String>;
    note: t.Nullable<t.String>;
    content: t.Nullable<t.String>;
    rawContent: t.Nullable<t.String>;
    status: t.Nullable<BaseActionStatus>;
    rescheduledAt: t.Nullable<t.DateTime>;
    rescheduledDays: t.Nullable<t.Int>;
    rescheduledFrom: t.Nullable<BaseActionType>;
    category: t.Nullable<BaseActionCategory>;
    createdBy: t.Nullable<UserType>;
    createdAt: t.DateTime;
    completedAt: t.Nullable<t.DateTime>;
    actionType: t.Nullable<t.String>;
    journeyUserReference: t.Nullable<JourneyUserReferenceType>;
    templateActionReference: t.Nullable<TemplateActionType>;
    baseactionPtr: BaseActionType;
    dailyReconnects: ActionCollectionType[];
    dailyFirstCalls: ActionCollectionType[];
    dailyAnamnesisCalls: ActionCollectionType[];
    journey: t.Nullable<JourneyType>;
    journeyTask: t.Nullable<t.Boolean>;
    translatedContent: t.Nullable<t.String>;
    translatedRawContent: t.Nullable<t.String>;
    rescheduledToDate: t.Nullable<t.Date>;
    rescheduledFromDate: t.Nullable<t.Date>;
    constructor() { this.__typename = ""; this.id = ""; this.customer = proxy(CustomerType); this.contact = proxy(ContactType); this.assignee = proxy(UserType); this.dueDate = null; this.deadlineDate = null; this.startDate = null; this.subject = null; this.emailSubject = null; this.note = null; this.content = null; this.rawContent = null; this.status = null; this.rescheduledAt = null; this.rescheduledDays = null; this.rescheduledFrom = proxy(BaseActionType); this.category = null; this.createdBy = proxy(UserType); this.createdAt = ""; this.completedAt = null; this.actionType = null; this.journeyUserReference = proxy(JourneyUserReferenceType); this.templateActionReference = proxy(TemplateActionType); this.baseactionPtr = proxy(BaseActionType); this.dailyReconnects = arrayProxy(ActionCollectionType); this.dailyFirstCalls = arrayProxy(ActionCollectionType); this.dailyAnamnesisCalls = arrayProxy(ActionCollectionType); this.journey = proxy(JourneyType); this.journeyTask = null; this.translatedContent = null; this.translatedRawContent = null; this.rescheduledToDate = null; this.rescheduledFromDate = null; }
}
export class DailyStorageType {
    __typename: t.String;
    id: t.ID;
    user: CustomerType;
    day: t.Date;
    actions: t.Nullable<ActionCollectionType>;
    motivation: t.Int;
    constructor() { this.__typename = ""; this.id = ""; this.user = proxy(CustomerType); this.day = ""; this.actions = proxy(ActionCollectionType); this.motivation = 0; }
}
export class DailyStorageTypeConnection {
    __typename: t.String;
    pageInfo: PageInfo;
    edges: DailyStorageTypeEdge[];
    constructor() { this.__typename = ""; this.pageInfo = proxy(PageInfo); this.edges = arrayProxy(DailyStorageTypeEdge); }
}
export class DailyStorageTypeEdge {
    __typename: t.String;
    node: t.Nullable<DailyStorageType>;
    cursor: t.String;
    constructor() { this.__typename = ""; this.node = proxy(DailyStorageType); this.cursor = ""; }
}
export class CommissionDetailTypeConnection {
    __typename: t.String;
    pageInfo: PageInfo;
    edges: CommissionDetailTypeEdge[];
    constructor() { this.__typename = ""; this.pageInfo = proxy(PageInfo); this.edges = arrayProxy(CommissionDetailTypeEdge); }
}
export class CommissionDetailTypeEdge {
    __typename: t.String;
    node: t.Nullable<CommissionDetailType>;
    cursor: t.String;
    constructor() { this.__typename = ""; this.node = proxy(CommissionDetailType); this.cursor = ""; }
}
export class CommissionDetailType {
    __typename: t.String;
    description: t.Nullable<t.String>;
    amount: t.Nullable<t.Float>;
    totalEarned: t.Nullable<t.Float>;
    dateCreated: t.Nullable<t.DateTime>;
    partner: t.Nullable<CustomerType>;
    commissionRun: CommissionRunType;
    id: t.ID;
    receiptBase64: t.Nullable<t.String>;
    constructor() { this.__typename = ""; this.description = null; this.amount = null; this.totalEarned = null; this.dateCreated = null; this.partner = proxy(CustomerType); this.commissionRun = proxy(CommissionRunType); this.id = ""; this.receiptBase64 = null; }
}
export class CommissionRunType {
    __typename: t.String;
    bydesignId: t.Nullable<t.Int>;
    startDate: t.Nullable<t.Date>;
    endDate: t.Nullable<t.Date>;
    zipArchive: t.Nullable<DocumentType>;
    createdAt: t.Nullable<t.DateTime>;
    monthly: t.Boolean;
    commissionDetails: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => CommissionDetailTypeConnection;
    id: t.ID;
    displayName: t.Nullable<t.String>;
    totalEarned: t.Nullable<t.Float>;
    totalPayouts: t.Nullable<t.Float>;
    constructor() { this.__typename = ""; this.bydesignId = null; this.startDate = null; this.endDate = null; this.zipArchive = proxy(DocumentType); this.createdAt = null; this.monthly = false; this.commissionDetails = fnProxy(CommissionDetailTypeConnection); this.id = ""; this.displayName = null; this.totalEarned = null; this.totalPayouts = null; }
}
export class AnamnesisProgressType {
    __typename: t.String;
    category: t.Nullable<AnamnesisCategoryType>;
    total: t.Nullable<t.Int>;
    required: t.Nullable<t.Int>;
    filledRequired: t.Nullable<t.Int>;
    filled: t.Nullable<t.Int>;
    constructor() { this.__typename = ""; this.category = proxy(AnamnesisCategoryType); this.total = null; this.required = null; this.filledRequired = null; this.filled = null; }
}
export class AnamnesisCategoryType {
    __typename: t.String;
    id: t.ID;
    name: t.String;
    color: t.Nullable<t.String>;
    icon: t.Nullable<t.String>;
    anamnesisFields: AnamnesisFieldType[];
    constructor() { this.__typename = ""; this.id = ""; this.name = ""; this.color = null; this.icon = null; this.anamnesisFields = arrayProxy(AnamnesisFieldType); }
}
export class AnamnesisFieldType {
    __typename: t.String;
    category: t.Nullable<AnamnesisCategoryType>;
    slug: t.String;
    name: t.Nullable<t.String>;
    helpText: t.Nullable<t.String>;
    inputType: t.Nullable<AnamnesisFieldInputType>;
    required: t.Boolean;
    options: t.Nullable<t.GenericScalar>;
    conditions: t.Nullable<t.GenericScalar>;
    constructor() { this.__typename = ""; this.category = proxy(AnamnesisCategoryType); this.slug = ""; this.name = null; this.helpText = null; this.inputType = null; this.required = false; this.options = null; this.conditions = null; }
}
export class OrderStatsType {
    __typename: t.String;
    totalOrders: t.Nullable<t.Int>;
    lifetimeValue: t.Nullable<t.Float>;
    averageOrderValue: t.Nullable<t.Float>;
    firstOrderDate: t.Nullable<t.Date>;
    lastOrderDate: t.Nullable<t.Date>;
    constructor() { this.__typename = ""; this.totalOrders = null; this.lifetimeValue = null; this.averageOrderValue = null; this.firstOrderDate = null; this.lastOrderDate = null; }
}
export class LearningAllocationType {
    __typename: t.String;
    user: UserType;
    overviewCategoryId: t.Nullable<t.String>;
    status: t.Nullable<t.String>;
    constructor() { this.__typename = ""; this.user = proxy(UserType); this.overviewCategoryId = null; this.status = null; }
}
export class LearningType {
    __typename: t.String;
    user: UserType;
    courseId: t.Nullable<t.String>;
    lessonId: t.Nullable<t.String>;
    status: t.Nullable<t.String>;
    createdAt: t.Nullable<t.DateTime>;
    completedAt: t.Nullable<t.DateTime>;
    constructor() { this.__typename = ""; this.user = proxy(UserType); this.courseId = null; this.lessonId = null; this.status = null; this.createdAt = null; this.completedAt = null; }
}
export class WishlistProductType {
    __typename: t.String;
    quantity: t.Nullable<t.Int>;
    productId: t.Nullable<t.String>;
    displayName: t.Nullable<t.String>;
    longDetail: t.Nullable<t.String>;
    price: t.Nullable<t.Float>;
    compare: t.Nullable<t.Float>;
    smallImageUrl: t.Nullable<t.String>;
    largeImageUrl: t.Nullable<t.String>;
    constructor() { this.__typename = ""; this.quantity = null; this.productId = null; this.displayName = null; this.longDetail = null; this.price = null; this.compare = null; this.smallImageUrl = null; this.largeImageUrl = null; }
}
export class TagType {
    __typename: t.String;
    name: t.Nullable<t.String>;
    customerCount: t.Nullable<t.Int>;
    constructor() { this.__typename = ""; this.name = null; this.customerCount = null; }
}
export class ContactTypeConnection {
    __typename: t.String;
    pageInfo: PageInfo;
    edges: ContactTypeEdge[];
    constructor() { this.__typename = ""; this.pageInfo = proxy(PageInfo); this.edges = arrayProxy(ContactTypeEdge); }
}
export class ContactTypeEdge {
    __typename: t.String;
    node: t.Nullable<ContactType>;
    cursor: t.String;
    constructor() { this.__typename = ""; this.node = proxy(ContactType); this.cursor = ""; }
}
export class ContentLibFolderTypeConnection {
    __typename: t.String;
    pageInfo: PageInfo;
    edges: ContentLibFolderTypeEdge[];
    constructor() { this.__typename = ""; this.pageInfo = proxy(PageInfo); this.edges = arrayProxy(ContentLibFolderTypeEdge); }
}
export class ContentLibFolderTypeEdge {
    __typename: t.String;
    node: t.Nullable<ContentLibFolderType>;
    cursor: t.String;
    constructor() { this.__typename = ""; this.node = proxy(ContentLibFolderType); this.cursor = ""; }
}
export class ContentLibFolderType {
    __typename: t.String;
    id: t.ID;
    title: t.Nullable<t.String>;
    parent: t.Nullable<ContentLibFolderType>;
    createdBy: t.Nullable<UserType>;
    createdAt: t.DateTime;
    changedBy: t.Nullable<UserType>;
    changedAt: t.DateTime;
    children: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
    }) => ContentLibFolderTypeConnection;
    entries: (args?: {
        before?: t.String;
        after?: t.String;
        first?: t.Int;
        last?: t.Int;
        filterTitle?: t.String;
        filterKey?: t.String;
    }) => ContentLibraryEntryTypeConnection;
    countEntries: t.Nullable<t.Int>;
    countChildrenFolders: t.Nullable<t.Int>;
    constructor() { this.__typename = ""; this.id = ""; this.title = null; this.parent = proxy(ContentLibFolderType); this.createdBy = proxy(UserType); this.createdAt = ""; this.changedBy = proxy(UserType); this.changedAt = ""; this.children = fnProxy(ContentLibFolderTypeConnection); this.entries = fnProxy(ContentLibraryEntryTypeConnection); this.countEntries = null; this.countChildrenFolders = null; }
}
export class ContentLibraryEntryTypeConnection {
    __typename: t.String;
    pageInfo: PageInfo;
    edges: ContentLibraryEntryTypeEdge[];
    constructor() { this.__typename = ""; this.pageInfo = proxy(PageInfo); this.edges = arrayProxy(ContentLibraryEntryTypeEdge); }
}
export class ContentLibraryEntryTypeEdge {
    __typename: t.String;
    node: t.Nullable<ContentLibraryEntryType>;
    cursor: t.String;
    constructor() { this.__typename = ""; this.node = proxy(ContentLibraryEntryType); this.cursor = ""; }
}
export class ContentLibraryEntryType {
    __typename: t.String;
    id: t.ID;
    title: t.Nullable<t.String>;
    description: t.Nullable<t.String>;
    content: t.Nullable<t.String>;
    folder: t.Nullable<ContentLibFolderType>;
    createdBy: t.Nullable<UserType>;
    createdAt: t.DateTime;
    changedBy: t.Nullable<UserType>;
    changedAt: t.DateTime;
    constructor() { this.__typename = ""; this.id = ""; this.title = null; this.description = null; this.content = null; this.folder = proxy(ContentLibFolderType); this.createdBy = proxy(UserType); this.createdAt = ""; this.changedBy = proxy(UserType); this.changedAt = ""; }
}
export class InfoSharingEntryTypeConnection {
    __typename: t.String;
    pageInfo: PageInfo;
    edges: InfoSharingEntryTypeEdge[];
    constructor() { this.__typename = ""; this.pageInfo = proxy(PageInfo); this.edges = arrayProxy(InfoSharingEntryTypeEdge); }
}
export class InfoSharingEntryTypeEdge {
    __typename: t.String;
    node: t.Nullable<InfoSharingEntryType>;
    cursor: t.String;
    constructor() { this.__typename = ""; this.node = proxy(InfoSharingEntryType); this.cursor = ""; }
}
export class InfoSharingEntryType {
    __typename: t.String;
    id: t.ID;
    title: t.Nullable<t.String>;
    description: t.Nullable<t.String>;
    base64: t.Nullable<t.String>;
    headingMail: t.Nullable<t.String>;
    contentMail: t.Nullable<t.String>;
    contentRawMail: t.Nullable<t.String>;
    contentWhatsapp: t.Nullable<t.String>;
    contentRawWhatsapp: t.Nullable<t.String>;
    contentSms: t.Nullable<t.String>;
    contentRawSms: t.Nullable<t.String>;
    contentSocial: t.Nullable<t.String>;
    contentRawSocial: t.Nullable<t.String>;
    contentPhone: t.Nullable<t.String>;
    contentRawPhone: t.Nullable<t.String>;
    forPartners: t.Boolean;
    forCoaches: t.Boolean;
    special: t.Boolean;
    createdBy: t.Nullable<UserType>;
    createdAt: t.DateTime;
    changedBy: t.Nullable<UserType>;
    changedAt: t.DateTime;
    constructor() { this.__typename = ""; this.id = ""; this.title = null; this.description = null; this.base64 = null; this.headingMail = null; this.contentMail = null; this.contentRawMail = null; this.contentWhatsapp = null; this.contentRawWhatsapp = null; this.contentSms = null; this.contentRawSms = null; this.contentSocial = null; this.contentRawSocial = null; this.contentPhone = null; this.contentRawPhone = null; this.forPartners = false; this.forCoaches = false; this.special = false; this.createdBy = proxy(UserType); this.createdAt = ""; this.changedBy = proxy(UserType); this.changedAt = ""; }
}
export class SnekEmailType {
    __typename: t.String;
    id: t.Nullable<t.ID>;
    emailAddress: t.Nullable<t.String>;
    constructor() { this.__typename = ""; this.id = null; this.emailAddress = null; }
}
export class CommissionRunTypeConnection {
    __typename: t.String;
    pageInfo: PageInfo;
    edges: CommissionRunTypeEdge[];
    constructor() { this.__typename = ""; this.pageInfo = proxy(PageInfo); this.edges = arrayProxy(CommissionRunTypeEdge); }
}
export class CommissionRunTypeEdge {
    __typename: t.String;
    node: t.Nullable<CommissionRunType>;
    cursor: t.String;
    constructor() { this.__typename = ""; this.node = proxy(CommissionRunType); this.cursor = ""; }
}
export class CommissionRunConnection {
    __typename: t.String;
    pageInfo: PageInfo;
    edges: CommissionRunEdge[];
    constructor() { this.__typename = ""; this.pageInfo = proxy(PageInfo); this.edges = arrayProxy(CommissionRunEdge); }
}
export class CommissionRunEdge {
    __typename: t.String;
    node: t.Nullable<CommissionRunType>;
    cursor: t.String;
    constructor() { this.__typename = ""; this.node = proxy(CommissionRunType); this.cursor = ""; }
}
export class CommissionDetailConnection {
    __typename: t.String;
    pageInfo: PageInfo;
    edges: CommissionDetailEdge[];
    constructor() { this.__typename = ""; this.pageInfo = proxy(PageInfo); this.edges = arrayProxy(CommissionDetailEdge); }
}
export class CommissionDetailEdge {
    __typename: t.String;
    node: t.Nullable<CommissionDetailType>;
    cursor: t.String;
    constructor() { this.__typename = ""; this.node = proxy(CommissionDetailType); this.cursor = ""; }
}
export class ActionMetricType {
    __typename: t.String;
    todayOpen: t.Nullable<t.Int>;
    futureOpen: t.Nullable<t.Int>;
    pastOpen: t.Nullable<t.Int>;
    recentlyDone: t.Nullable<t.Int>;
    constructor() { this.__typename = ""; this.todayOpen = null; this.futureOpen = null; this.pastOpen = null; this.recentlyDone = null; }
}
export class CategoryType {
    __typename: t.String;
    color: t.Nullable<t.String>;
    title: t.Nullable<t.String>;
    slug: t.Nullable<t.String>;
    countInit: t.Nullable<t.Int>;
    count: t.Nullable<t.Int>;
    parentCategory: t.Nullable<t.String>;
    parentCategorySlug: t.Nullable<t.String>;
    content: t.Nullable<t.CategoryContentType>[];
    tasks: t.Nullable<ActionType>[];
    dailyContactImport: t.Nullable<DailyContactImportType>[];
    dailyContactQualify: t.Nullable<DailyContactQualifyType>[];
    dailyCustomer: t.Nullable<DailyCustomerType>[];
    dailyJourneyGroup: t.Nullable<DailyJourneyGroupType>[];
    constructor() { this.__typename = ""; this.color = null; this.title = null; this.slug = null; this.countInit = null; this.count = null; this.parentCategory = null; this.parentCategorySlug = null; this.content = []; this.tasks = arrayProxy(ActionType); this.dailyContactImport = arrayProxy(DailyContactImportType); this.dailyContactQualify = arrayProxy(DailyContactQualifyType); this.dailyCustomer = arrayProxy(DailyCustomerType); this.dailyJourneyGroup = arrayProxy(DailyJourneyGroupType); }
}
export class DailyContactImportType {
    __typename: t.String;
    title: t.Nullable<t.String>;
    slug: t.Nullable<t.String>;
    countInit: t.Nullable<t.Int>;
    countReached: t.Nullable<t.Int>;
    count: t.Nullable<t.Int>;
    countContacts: t.Nullable<t.Int>;
    description: t.Nullable<t.String>;
    constructor() { this.__typename = ""; this.title = null; this.slug = null; this.countInit = null; this.countReached = null; this.count = null; this.countContacts = null; this.description = null; }
}
export class DailyContactQualifyType {
    __typename: t.String;
    title: t.Nullable<t.String>;
    slug: t.Nullable<t.String>;
    contacts: t.Nullable<ContactType>[];
    description: t.Nullable<t.String>;
    constructor() { this.__typename = ""; this.title = null; this.slug = null; this.contacts = arrayProxy(ContactType); this.description = null; }
}
export class DailyCustomerType {
    __typename: t.String;
    title: t.Nullable<t.String>;
    slug: t.Nullable<t.String>;
    customers: t.Nullable<CustomerType>[];
    description: t.Nullable<t.String>;
    constructor() { this.__typename = ""; this.title = null; this.slug = null; this.customers = arrayProxy(CustomerType); this.description = null; }
}
export class DailyJourneyGroupType {
    __typename: t.String;
    title: t.Nullable<t.String>;
    slug: t.Nullable<t.String>;
    countInit: t.Nullable<t.Int>;
    count: t.Nullable<t.Int>;
    description: t.Nullable<t.String>;
    tasks: t.Nullable<ActionType>[];
    constructor() { this.__typename = ""; this.title = null; this.slug = null; this.countInit = null; this.count = null; this.description = null; this.tasks = arrayProxy(ActionType); }
}
export class AnamnesisCategoryTypeConnection {
    __typename: t.String;
    pageInfo: PageInfo;
    edges: AnamnesisCategoryTypeEdge[];
    constructor() { this.__typename = ""; this.pageInfo = proxy(PageInfo); this.edges = arrayProxy(AnamnesisCategoryTypeEdge); }
}
export class AnamnesisCategoryTypeEdge {
    __typename: t.String;
    node: t.Nullable<AnamnesisCategoryType>;
    cursor: t.String;
    constructor() { this.__typename = ""; this.node = proxy(AnamnesisCategoryType); this.cursor = ""; }
}
export class AnamnesisOrderingType {
    __typename: t.String;
    categoryOrder: t.Nullable<t.GenericScalar>;
    fieldOrder: t.Nullable<t.GenericScalar>;
    constructor() { this.__typename = ""; this.categoryOrder = null; this.fieldOrder = null; }
}
export class CustomerConnection {
    __typename: t.String;
    pageInfo: PageInfo;
    edges: CustomerEdge[];
    totalCount: t.Nullable<t.Int>;
    constructor() { this.__typename = ""; this.pageInfo = proxy(PageInfo); this.edges = arrayProxy(CustomerEdge); this.totalCount = null; }
}
export class CustomerEdge {
    __typename: t.String;
    node: t.Nullable<CustomerType>;
    cursor: t.String;
    constructor() { this.__typename = ""; this.node = proxy(CustomerType); this.cursor = ""; }
}
export class UserTypeConnection {
    __typename: t.String;
    pageInfo: PageInfo;
    edges: UserTypeEdge[];
    constructor() { this.__typename = ""; this.pageInfo = proxy(PageInfo); this.edges = arrayProxy(UserTypeEdge); }
}
export class UserTypeEdge {
    __typename: t.String;
    node: t.Nullable<UserType>;
    cursor: t.String;
    constructor() { this.__typename = ""; this.node = proxy(UserType); this.cursor = ""; }
}
export class BanCodeType {
    __typename: t.String;
    code: t.Nullable<t.Int>;
    info: t.Nullable<t.String>;
    constructor() { this.__typename = ""; this.code = null; this.info = null; }
}
export class Mutation {
    __typename: t.String;
    triggerCommissionRun: (args?: {
        bydesignId?: t.Int;
    }) => t.Nullable<TriggerCommissionRun>;
    p5mailAddMailAccount: (args?: {
        emailAddress?: t.String;
        smtpConfig?: SMTPCredentialInput;
    }) => t.Nullable<AddMailAccount>;
    p5mailRemoveMailConnection: (args?: {
        emailId?: t.UUID;
    }) => t.Nullable<RemoveMailConnection>;
    p5mailScheduleMail: (args: {
        body?: t.String;
        subject?: t.String;
        to: t.String;
    }) => t.Nullable<ScheduleMail>;
    learningSetStatus: (args?: {
        courseId?: t.String;
        lessonId?: t.String;
        status?: t.String;
    }) => t.Nullable<SetStatus>;
    learningAllocate: (args: {
        customerId: t.ID;
        overviewCategoryId: t.String;
        status: Status;
    }) => t.Nullable<AllocateLearning>;
    freedomCreateOrderDraft: (args: {
        customerId: t.ID;
        message?: t.String;
        productId: t.String;
    }) => t.Nullable<CreateOrderDraft>;
    contentlibCreateEntry: (args: {
        content: t.String;
        description?: t.String;
        folderId?: t.ID;
        title: t.String;
    }) => t.Nullable<CreateContentLibEntry>;
    contentlibUpdateEntry: (args: {
        content?: t.String;
        delete?: t.Boolean;
        description?: t.String;
        folderId?: t.ID;
        id: t.ID;
        title?: t.String;
    }) => t.Nullable<UpdateContentLibEntry>;
    contentlibCreateSharingEntry: (args: {
        base64?: t.String;
        contentMail?: t.String;
        contentPhone?: t.String;
        contentRawMail?: t.String;
        contentRawPhone?: t.String;
        contentRawSms?: t.String;
        contentRawSocial?: t.String;
        contentRawWhatsapp?: t.String;
        contentSms?: t.String;
        contentSocial?: t.String;
        contentWhatsapp?: t.String;
        description?: t.String;
        forCoaches?: t.Boolean;
        forPartners?: t.Boolean;
        headingMail?: t.String;
        special?: t.Boolean;
        title: t.String;
    }) => t.Nullable<CreateInfoSharingEntry>;
    contentlibUpdateSharingEntry: (args: {
        base64?: t.String;
        contentMail?: t.String;
        contentPhone?: t.String;
        contentRawMail?: t.String;
        contentRawPhone?: t.String;
        contentRawSms?: t.String;
        contentRawSocial?: t.String;
        contentRawWhatsapp?: t.String;
        contentSms?: t.String;
        contentSocial?: t.String;
        contentWhatsapp?: t.String;
        delete?: t.Boolean;
        description?: t.String;
        headingMail?: t.String;
        id: t.ID;
        special?: t.Boolean;
        title?: t.String;
    }) => t.Nullable<UpdateInfoSharingEntry>;
    contentlibTranslateEntry: (args: {
        customerId: t.ID;
        template: t.String;
    }) => t.Nullable<TranslateContentLibEntry>;
    journeyCreateJourney: (args: {
        description?: t.String;
        title: t.String;
    }) => t.Nullable<CreateJourneyMutation>;
    journeyUpdateJourney: (args: {
        delete?: t.Boolean;
        description?: t.String;
        folderId?: t.ID;
        id: t.ID;
        mydayCategory?: t.String;
        status?: t.String;
        title?: t.String;
    }) => t.Nullable<UpdateJourneyMutation>;
    journeyCreateJourneyAction: (args: {
        input: CreateTemplateTaskInput;
        journeyId: t.ID;
    }) => t.Nullable<CreateJourneyActionMutation>;
    journeyUpdateJourneyAction: (args: {
        delete?: t.Boolean;
        id: t.ID;
        input?: UpdateTemplateTaskInput;
    }) => t.Nullable<UpdateJourneyActionMutation>;
    journeyHandleUser: (args: {
        action: t.String;
        baseDate?: t.Date;
        customerId: t.ID;
        journeyId: t.ID;
    }) => t.Nullable<UserJourneyMutation>;
    actionCreateAction: (args: {
        actionType: t.String;
        input?: ActionInput;
    }) => t.Nullable<CreateAction>;
    actionUpdateAction: (args: {
        actionId: t.ID;
        status: t.String;
    }) => t.Nullable<UpdateAction>;
    actionCompleteDailyAction: (args: {
        actionId: t.ID;
    }) => t.Nullable<CompleteDailyAction>;
    actionReschedule: (args: {
        actionId: t.ID;
        baseType: t.String;
        days: t.Int;
    }) => t.Nullable<Reschedule>;
    contactCreateContact: (args: {
        input: ContactInputType;
    }) => t.Nullable<CreateContact>;
    contactEditContact: (args: {
        delete?: t.Boolean;
        id?: t.ID;
        input: ContactInputType;
    }) => t.Nullable<EditContact>;
    customerCustomer: (args: {
        contactToDelete?: t.ID;
        id?: t.ID;
        input: CustomerInput;
        me?: t.Boolean;
    }) => t.Nullable<HandleCustomer>;
    customerActivity: (args: {
        action: t.String;
        activityId?: t.ID;
        customerId: t.ID;
        input?: ActivityInput;
    }) => t.Nullable<HandleActivity>;
    customerUpdateAnamnesis: (args: {
        customerId: t.ID;
        input: AnamnesisDataInput;
    }) => t.Nullable<UpdateAnamnesisData>;
    customerBulkUpdateAnamnesis: (args: {
        customerId: t.ID;
        draft?: t.Boolean;
        finish?: t.Boolean;
        input?: AnamnesisDataInput;
    }) => t.Nullable<BulkUpdateAnamnesisData>;
    customerHandleTag: (args: {
        action: t.String;
        customerId: t.ID;
        tag: t.String;
    }) => t.Nullable<HandleTag>;
    customerHandleBeautyreport: (args: {
        action: t.String;
        customerId: t.ID;
        documentId?: t.ID;
        meta?: DocumentMetaInput;
    }) => t.Nullable<HandleDocupilot>;
    customerTriggerOrder: (args: {
        orderId: t.Int;
    }) => t.Nullable<TriggerOrder>;
    userUpdate: (args: {
        input: DetailedUserInput;
    }) => t.Nullable<UpdateUser>;
    userLogin: (args: {
        username: t.String;
        password: t.String;
    }) => t.Nullable<ObtainJSONWebToken>;
    userLogout: (args: {
        refreshToken: t.String;
    }) => t.Nullable<Revoke>;
    userLogoutAll: t.Nullable<RevokeAll>;
    userVerifyToken: (args: {
        token: t.String;
    }) => t.Nullable<Verify>;
    userRefreshToken: (args: {
        refreshToken: t.String;
    }) => t.Nullable<Refresh>;
    constructor() { this.__typename = ""; this.triggerCommissionRun = fnProxy(TriggerCommissionRun); this.p5mailAddMailAccount = fnProxy(AddMailAccount); this.p5mailRemoveMailConnection = fnProxy(RemoveMailConnection); this.p5mailScheduleMail = fnProxy(ScheduleMail); this.learningSetStatus = fnProxy(SetStatus); this.learningAllocate = fnProxy(AllocateLearning); this.freedomCreateOrderDraft = fnProxy(CreateOrderDraft); this.contentlibCreateEntry = fnProxy(CreateContentLibEntry); this.contentlibUpdateEntry = fnProxy(UpdateContentLibEntry); this.contentlibCreateSharingEntry = fnProxy(CreateInfoSharingEntry); this.contentlibUpdateSharingEntry = fnProxy(UpdateInfoSharingEntry); this.contentlibTranslateEntry = fnProxy(TranslateContentLibEntry); this.journeyCreateJourney = fnProxy(CreateJourneyMutation); this.journeyUpdateJourney = fnProxy(UpdateJourneyMutation); this.journeyCreateJourneyAction = fnProxy(CreateJourneyActionMutation); this.journeyUpdateJourneyAction = fnProxy(UpdateJourneyActionMutation); this.journeyHandleUser = fnProxy(UserJourneyMutation); this.actionCreateAction = fnProxy(CreateAction); this.actionUpdateAction = fnProxy(UpdateAction); this.actionCompleteDailyAction = fnProxy(CompleteDailyAction); this.actionReschedule = fnProxy(Reschedule); this.contactCreateContact = fnProxy(CreateContact); this.contactEditContact = fnProxy(EditContact); this.customerCustomer = fnProxy(HandleCustomer); this.customerActivity = fnProxy(HandleActivity); this.customerUpdateAnamnesis = fnProxy(UpdateAnamnesisData); this.customerBulkUpdateAnamnesis = fnProxy(BulkUpdateAnamnesisData); this.customerHandleTag = fnProxy(HandleTag); this.customerHandleBeautyreport = fnProxy(HandleDocupilot); this.customerTriggerOrder = fnProxy(TriggerOrder); this.userUpdate = fnProxy(UpdateUser); this.userLogin = fnProxy(ObtainJSONWebToken); this.userLogout = fnProxy(Revoke); this.userLogoutAll = proxy(RevokeAll); this.userVerifyToken = fnProxy(Verify); this.userRefreshToken = fnProxy(Refresh); }
}
export class TriggerCommissionRun {
    __typename: t.String;
    result: t.Nullable<t.GenericScalar>;
    commissionRun: t.Nullable<CommissionRunType>;
    constructor() { this.__typename = ""; this.result = null; this.commissionRun = proxy(CommissionRunType); }
}
export class AddMailAccount {
    __typename: t.String;
    ok: t.Nullable<t.Boolean>;
    constructor() { this.__typename = ""; this.ok = null; }
}
export class RemoveMailConnection {
    __typename: t.String;
    ok: t.Nullable<t.Boolean>;
    constructor() { this.__typename = ""; this.ok = null; }
}
export class ScheduleMail {
    __typename: t.String;
    ok: t.Nullable<t.Boolean>;
    constructor() { this.__typename = ""; this.ok = null; }
}
export class SetStatus {
    __typename: t.String;
    learning: t.Nullable<LearningType>;
    constructor() { this.__typename = ""; this.learning = proxy(LearningType); }
}
export class AllocateLearning {
    __typename: t.String;
    learningAllocation: t.Nullable<LearningAllocationType>;
    constructor() { this.__typename = ""; this.learningAllocation = proxy(LearningAllocationType); }
}
export class CreateOrderDraft {
    __typename: t.String;
    ok: t.Nullable<t.Boolean>;
    constructor() { this.__typename = ""; this.ok = null; }
}
export class CreateContentLibEntry {
    __typename: t.String;
    ok: t.Nullable<t.Boolean>;
    entry: t.Nullable<ContentLibraryEntryType>;
    error: t.Nullable<ErrorType>;
    constructor() { this.__typename = ""; this.ok = null; this.entry = proxy(ContentLibraryEntryType); this.error = proxy(ErrorType); }
}
export class ErrorType {
    __typename: t.String;
    message: t.Nullable<t.String>;
    code: t.Nullable<t.Int>;
    constructor() { this.__typename = ""; this.message = null; this.code = null; }
}
export class UpdateContentLibEntry {
    __typename: t.String;
    ok: t.Nullable<t.Boolean>;
    entry: t.Nullable<ContentLibraryEntryType>;
    error: t.Nullable<ErrorType>;
    constructor() { this.__typename = ""; this.ok = null; this.entry = proxy(ContentLibraryEntryType); this.error = proxy(ErrorType); }
}
export class CreateInfoSharingEntry {
    __typename: t.String;
    entry: t.Nullable<InfoSharingEntryType>;
    constructor() { this.__typename = ""; this.entry = proxy(InfoSharingEntryType); }
}
export class UpdateInfoSharingEntry {
    __typename: t.String;
    entry: t.Nullable<InfoSharingEntryType>;
    constructor() { this.__typename = ""; this.entry = proxy(InfoSharingEntryType); }
}
export class TranslateContentLibEntry {
    __typename: t.String;
    content: t.Nullable<t.String>;
    constructor() { this.__typename = ""; this.content = null; }
}
export class CreateJourneyMutation {
    __typename: t.String;
    ok: t.Nullable<t.Boolean>;
    journey: t.Nullable<JourneyType>;
    error: t.Nullable<ErrorType>;
    constructor() { this.__typename = ""; this.ok = null; this.journey = proxy(JourneyType); this.error = proxy(ErrorType); }
}
export class UpdateJourneyMutation {
    __typename: t.String;
    journey: t.Nullable<JourneyType>;
    constructor() { this.__typename = ""; this.journey = proxy(JourneyType); }
}
export class CreateJourneyActionMutation {
    __typename: t.String;
    ok: t.Nullable<t.Boolean>;
    action: t.Nullable<TemplateActionType>;
    error: t.Nullable<ErrorType>;
    constructor() { this.__typename = ""; this.ok = null; this.action = proxy(TemplateActionType); this.error = proxy(ErrorType); }
}
export class UpdateJourneyActionMutation {
    __typename: t.String;
    ok: t.Nullable<t.Boolean>;
    action: t.Nullable<TemplateActionType>;
    error: t.Nullable<ErrorType>;
    constructor() { this.__typename = ""; this.ok = null; this.action = proxy(TemplateActionType); this.error = proxy(ErrorType); }
}
export class UserJourneyMutation {
    __typename: t.String;
    ok: t.Nullable<t.Boolean>;
    reference: t.Nullable<JourneyUserReferenceType>;
    constructor() { this.__typename = ""; this.ok = null; this.reference = proxy(JourneyUserReferenceType); }
}
export class CreateAction {
    __typename: t.String;
    ok: t.Nullable<t.Boolean>;
    action: t.Nullable<ActionType>;
    error: t.Nullable<ErrorType>;
    constructor() { this.__typename = ""; this.ok = null; this.action = proxy(ActionType); this.error = proxy(ErrorType); }
}
export class UpdateAction {
    __typename: t.String;
    ok: t.Nullable<t.Boolean>;
    action: t.Nullable<ActionType>;
    error: t.Nullable<ErrorType>;
    constructor() { this.__typename = ""; this.ok = null; this.action = proxy(ActionType); this.error = proxy(ErrorType); }
}
export class CompleteDailyAction {
    __typename: t.String;
    action: t.Nullable<DailyActionType>;
    constructor() { this.__typename = ""; this.action = proxy(DailyActionType); }
}
export class Reschedule {
    __typename: t.String;
    action: t.Nullable<ActionType>;
    constructor() { this.__typename = ""; this.action = proxy(ActionType); }
}
export class CreateContact {
    __typename: t.String;
    contact: t.Nullable<ContactType>;
    constructor() { this.__typename = ""; this.contact = proxy(ContactType); }
}
export class EditContact {
    __typename: t.String;
    contact: t.Nullable<ContactType>;
    constructor() { this.__typename = ""; this.contact = proxy(ContactType); }
}
export class HandleCustomer {
    __typename: t.String;
    ok: t.Nullable<t.Boolean>;
    customer: t.Nullable<CustomerType>;
    error: t.Nullable<ErrorType>;
    constructor() { this.__typename = ""; this.ok = null; this.customer = proxy(CustomerType); this.error = proxy(ErrorType); }
}
export class HandleActivity {
    __typename: t.String;
    ok: t.Nullable<t.Boolean>;
    activity: t.Nullable<ActivityType>;
    error: t.Nullable<ErrorType>;
    constructor() { this.__typename = ""; this.ok = null; this.activity = proxy(ActivityType); this.error = proxy(ErrorType); }
}
export class UpdateAnamnesisData {
    __typename: t.String;
    ok: t.Nullable<t.Boolean>;
    customer: t.Nullable<CustomerType>;
    error: t.Nullable<ErrorType>;
    constructor() { this.__typename = ""; this.ok = null; this.customer = proxy(CustomerType); this.error = proxy(ErrorType); }
}
export class BulkUpdateAnamnesisData {
    __typename: t.String;
    ok: t.Nullable<t.Boolean>;
    customer: t.Nullable<CustomerType>;
    error: t.Nullable<ErrorType>;
    constructor() { this.__typename = ""; this.ok = null; this.customer = proxy(CustomerType); this.error = proxy(ErrorType); }
}
export class HandleTag {
    __typename: t.String;
    ok: t.Nullable<t.Boolean>;
    tag: t.Nullable<TagType>;
    error: t.Nullable<ErrorType>;
    constructor() { this.__typename = ""; this.ok = null; this.tag = proxy(TagType); this.error = proxy(ErrorType); }
}
export class HandleDocupilot {
    __typename: t.String;
    ok: t.Nullable<t.Boolean>;
    document: t.Nullable<CharmDocumentType>;
    error: t.Nullable<ErrorType>;
    constructor() { this.__typename = ""; this.ok = null; this.document = proxy(CharmDocumentType); this.error = proxy(ErrorType); }
}
export class TriggerOrder {
    __typename: t.String;
    order: t.Nullable<OrderType>;
    constructor() { this.__typename = ""; this.order = proxy(OrderType); }
}
export class UpdateUser {
    __typename: t.String;
    user: t.Nullable<UserType>;
    constructor() { this.__typename = ""; this.user = proxy(UserType); }
}
export class ObtainJSONWebToken {
    __typename: t.String;
    token: t.Nullable<t.String>;
    refreshToken: t.Nullable<t.String>;
    constructor() { this.__typename = ""; this.token = null; this.refreshToken = null; }
}
export class Revoke {
    __typename: t.String;
    revoked: t.Nullable<t.Int>;
    constructor() { this.__typename = ""; this.revoked = null; }
}
export class RevokeAll {
    __typename: t.String;
    ok: t.Nullable<t.Boolean>;
    error: t.Nullable<ErrorType>;
    constructor() { this.__typename = ""; this.ok = null; this.error = proxy(ErrorType); }
}
export class Verify {
    __typename: t.String;
    payload: t.Nullable<t.GenericScalar>;
    constructor() { this.__typename = ""; this.payload = null; }
}
export class Refresh {
    __typename: t.String;
    token: t.Nullable<t.String>;
    payload: t.Nullable<t.GenericScalar>;
    refreshToken: t.Nullable<t.String>;
    constructor() { this.__typename = ""; this.token = null; this.payload = null; this.refreshToken = null; }
}
export class Subscription {
    __typename: t.String;
    userUser: (args: {
        id: t.ID;
    }) => t.Nullable<UserType>;
    userMe: t.Nullable<UserType>;
    constructor() { this.__typename = ""; this.userUser = fnProxy(UserType); this.userMe = proxy(UserType); }
}

